import {
  AddExchangeRequest,
  AddExchangeResponse,
} from "@services/models/InvoiceApprovals/AddExchangeModel";
import {
  AddOfframpBankRequest,
  AddOfframpBankResponse,
} from "@services/models/InvoiceApprovals/AddOfframpBankModel";
import {
  DeleteExchangeRequest,
  DeleteExchangeResponse,
} from "@services/models/InvoiceApprovals/DeleteExchangeModel";
import { GetExchangesResponse } from "@services/models/InvoiceApprovals/GetExchangesModel";
import {
  GetInvoiceApprovalsRequest,
  GetInvoiceApprovalsResponse,
} from "@services/models/InvoiceApprovals/GetInvoiceApprovalsModel";
import {
  GetOfframpBanksRequest,
  GetOfframpBanksResponse,
} from "@services/models/InvoiceApprovals/GetOfframpBanksModel";
import {
  ActivateDeactivateOfframpChargeResponse,
  ActivateOfframpChargeRequest,
  AddOfframpChargeRequest,
  AddOfframpChargeResponse,
  DeactivateOfframpChargeRequest,
  GetOfframpChargesRequest,
  GetOfframpChargesResponse,
  UpdateOfframpChargeRequest,
  UpdateOfframpChargeResponse,
} from "@services/models/InvoiceApprovals/GetOfframpChargesModel";
import {
  GetSupportedBaseCurrenciesRequest,
  GetSupportedBaseCurrenciesResponse,
} from "@services/models/InvoiceApprovals/GetSupportedBaseCurrencies";
import { GetUnserInvoiceBankResponse } from "@services/models/InvoiceApprovals/GetUnserInvoiceBankModel";
import {
  MarkInvoiceRequestIncompleteRequest,
  MarkInvoiceRequestIncompleteResponse,
} from "@services/models/InvoiceApprovals/MarkInvoiceRequestIncompleteModel";
import {
  ProcessInvoiceSettlementRequest,
  ProcessInvoiceSettlementResponse,
} from "@services/models/InvoiceApprovals/ProcessInvoiceSettlementModel";
import {
  UpdateExchangeRequest,
  UpdateExchangeResponse,
} from "@services/models/InvoiceApprovals/UpdateExchangeModel";
import {
  UpdateInvoiceApprovalRequest,
  UpdateInvoiceApprovalResponse,
} from "@services/models/InvoiceApprovals/UpdateInvoiceApprovalModel";
import {
  UpdateInvoiceTransactionDataRequest,
  UpdateInvoiceTransactionDataResponse,
} from "@services/models/InvoiceApprovals/UpdateInvoiceTransactionData";
import {
  ActivateDeactivateBankRequest,
  ActivateDeactivateBankResponse,
  UpdateOfframpBankRequest,
  UpdateOfframpBankResponse,
} from "@services/models/InvoiceApprovals/UpdateOfframpBankModel";
import { createApiClient } from "../api/createApiClient";
import { AxiosInstance } from "axios";

class InvoiceApiService {
  private api: AxiosInstance;
  constructor() {
    this.api = createApiClient();
  }

  public async getInvoiceRequests(
    data?: GetInvoiceApprovalsRequest
  ): Promise<GetInvoiceApprovalsResponse> {
    try {
      const params: Record<string, any> = {};
      if (data) {
        params.limit = data.limit;
        params.page = data.page;
        params.merchantId = data.merchantId;
        params.userId = data.userId;
        params.status = data.status;
      }

      const response = await this.api.get<GetInvoiceApprovalsResponse>(
        "/adminPanel/getInvoiceRequests",
        {
          params,
        }
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async approveInvoice(
    data: ApproveInvoiceRequest
  ): Promise<InvoiceActionResponse> {
    try {
      const response = await this.api.post<InvoiceActionResponse>(
        "/adminPanel/approveInvoiceRequest",
        data
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async rejectInvoice(
    data: RejectInvoiceRequest
  ): Promise<InvoiceActionResponse> {
    try {
      const response = await this.api.post<InvoiceActionResponse>(
        "/adminPanel/rejectInvoiceRequest",
        data
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateInvoiceRequestData(
    data: UpdateInvoiceApprovalRequest
  ): Promise<UpdateInvoiceApprovalResponse> {
    try {
      const response = await this.api.put<UpdateInvoiceApprovalResponse>(
        "/adminPanel/updateInvoiceRequestData",
        {
          requestId: data.requestId,
          requestData: data.requestData,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateInvoiceTransactionData(
    data: UpdateInvoiceTransactionDataRequest
  ): Promise<UpdateInvoiceTransactionDataResponse> {
    try {
      const response = await this.api.put<UpdateInvoiceTransactionDataResponse>(
        "/adminPanel/updateInvoiceRequestTransactionData",
        {
          requestId: data.requestId,
          transactionData: data.transactionData,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async processOfframpInvoiceSettlement(
    data: ProcessInvoiceSettlementRequest
  ): Promise<ProcessInvoiceSettlementResponse> {
    try {
      const response = await this.api.post<ProcessInvoiceSettlementResponse>(
        "/adminPanel/processOfframpInvoiceSettlement",
        {
          invoiceId: data.invoiceId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async markInvoiceRequestIncomplete(
    data: MarkInvoiceRequestIncompleteRequest
  ): Promise<MarkInvoiceRequestIncompleteResponse> {
    try {
      const response =
        await this.api.post<MarkInvoiceRequestIncompleteResponse>(
          "/adminPanel/markInvoiceRequestIncomplete",
          {
            requestId: data.requestId,
          }
        );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getExchanges(): Promise<GetExchangesResponse> {
    try {
      const response = await this.api.get<GetExchangesResponse>(
        "/adminPanel/getExchanges"
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async addExchange(
    data: AddExchangeRequest
  ): Promise<AddExchangeResponse> {
    try {
      const response = await this.api.post<AddExchangeResponse>(
        "/adminPanel/addExchange",
        {
          token: data.token,
          exchange: data.exchange,
          network: data.network,
          address: data.address,
          isActive: true,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateExchange(
    data: UpdateExchangeRequest
  ): Promise<UpdateExchangeResponse> {
    try {
      const response = await this.api.put<UpdateExchangeResponse>(
        "/adminPanel/updateExchange",
        {
          exchangeId: data.exchangeId,
          token: data.token,
          exchange: data.exchange,
          network: data.network,
          address: data.address,
          isActive: data.isActive,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Offramp Bank Management APIs
  public async getOfframpBanks(
    data?: GetOfframpBanksRequest
  ): Promise<GetOfframpBanksResponse> {
    try {
      const params: Record<string, any> = {};
      if (data) {
        params.page = data.page;
        params.rows = data.rows;
        params.bankName = data.bankName;
        params.accountType = data.accountType;
        params.isActive = data.isActive;
      }

      const response = await this.api.get<GetOfframpBanksResponse>(
        "/adminPanel/getOfframpBanks",
        {
          params,
        }
      );

      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getUserInvoiceBankAccounts(): Promise<GetUnserInvoiceBankResponse> {
    try {
      const response = await this.api.get<GetUnserInvoiceBankResponse>(
        "/adminPanel/getUserInvoiceBankAccounts"
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async addOfframpBank(
    data: AddOfframpBankRequest
  ): Promise<AddOfframpBankResponse> {
    try {
      const response = await this.api.post<AddOfframpBankResponse>(
        "/adminPanel/addOfframpBank",
        {
          bankName: data.bankName,
          accountNo: data.accountNo,
          accountName: data.accountName,
          accountType: data.accountType,
          isActive: data.isActive ?? true,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateOfframpBank(
    data: UpdateOfframpBankRequest
  ): Promise<UpdateOfframpBankResponse> {
    try {
      const response = await this.api.put<UpdateOfframpBankResponse>(
        "/adminPanel/updateOfframpBank",
        {
          bankId: data.bankId,
          bankName: data.bankName,
          accountNo: data.accountNo,
          accountName: data.accountName,
          accountType: data.accountType,
          isActive: data.isActive,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async activateOfframpBank(
    data: ActivateDeactivateBankRequest
  ): Promise<ActivateDeactivateBankResponse> {
    try {
      const response = await this.api.put<ActivateDeactivateBankResponse>(
        "/adminPanel/activateOfframpBank",
        {
          bankId: data.bankId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async deactivateOfframpBank(
    data: ActivateDeactivateBankRequest
  ): Promise<ActivateDeactivateBankResponse> {
    try {
      const response = await this.api.put<ActivateDeactivateBankResponse>(
        "/adminPanel/deactivateOfframpBank",
        {
          bankId: data.bankId,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  // Offramp Charges APIs
  public async getOfframpCharges(
    params?: GetOfframpChargesRequest
  ): Promise<GetOfframpChargesResponse> {
    try {
      const response = await this.api.get<GetOfframpChargesResponse>(
        "/adminPanel/getOfframpCharges",
        { params }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async addOfframpCharge(
    data: AddOfframpChargeRequest
  ): Promise<AddOfframpChargeResponse> {
    try {
      const response = await this.api.post<AddOfframpChargeResponse>(
        "/adminPanel/addOfframpCharge",
        data
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async updateOfframpCharge(
    data: UpdateOfframpChargeRequest
  ): Promise<UpdateOfframpChargeResponse> {
    try {
      const response = await this.api.put<UpdateOfframpChargeResponse>(
        "/adminPanel/updateOfframpCharge",
        data
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async activateOfframpCharge(
    data: ActivateOfframpChargeRequest
  ): Promise<ActivateDeactivateOfframpChargeResponse> {
    try {
      const response =
        await this.api.put<ActivateDeactivateOfframpChargeResponse>(
          "/adminPanel/activateOfframpCharge",
          data
        );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async deactivateOfframpCharge(
    data: DeactivateOfframpChargeRequest
  ): Promise<ActivateDeactivateOfframpChargeResponse> {
    try {
      const response =
        await this.api.put<ActivateDeactivateOfframpChargeResponse>(
          "/adminPanel/deactivateOfframpCharge",
          data
        );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async getSupportedBaseCurrenciesList(
    data?: GetSupportedBaseCurrenciesRequest
  ): Promise<GetSupportedBaseCurrenciesResponse> {
    try {
      const response = await this.api.get<GetSupportedBaseCurrenciesResponse>(
        "/invoices/getSupportedBaseCurrenciesList",
        {
          params: {
            isOffRampSupported: data?.isOffRampSupported ?? undefined,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  public async deleteExchange(
    data: DeleteExchangeRequest
  ): Promise<DeleteExchangeResponse> {
    try {
      const response = await this.api.delete<DeleteExchangeResponse>(
        "/adminPanel/deleteExchange",
        { data }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export const invoiceServices = new InvoiceApiService();
